import { createApp, reactive, defineAsyncComponent } from 'vue'
import app from '@/core/app'
import config from '@/core/config'
import _data from '@/core/_data'
import { capitalize, isset, each, viewport } from '@/core/utils'
import sapoHash from '@/core/hash'
import sapoQuery from '@/core/query'

const selector = value => `${config.selector}-${value || ''}`;

const dev = process.env.NODE_ENV != 'production';

const beta = !window.location.hash ? null : window.location.hash.includes('beta=1');

let elements = document.querySelectorAll(`[${selector('id')}]`);

const path = !dev ? process.env.VUE_APP_URL : '/';

let stored = {};

let delays = {};

let components = {};

let active = {};

config.components.forEach(component => (components[component] = defineAsyncComponent(() => import(`@/components/${component}.vue`))));

window.$_swsk = {
    events: {
        scrollable: status => (!status ? window.$_swsk.body.setAttribute(config.scrollable, true) : window.$_swsk.body.removeAttribute(config.scrollable)),
    },
    temp: {},
    widgets: {},
    state: {},
    viewport: [],
    active: [],
    refs: [],
    hash: sapoHash,
    query: sapoQuery,
    dev: status => (window.$_swsk.temp.dev = !isset(status) ? !window.$_swsk.temp.dev : status),
    log: (title, value) => (window.$_swsk.temp.dev && console.log(title, value))
};

['body', 'head'].forEach(tag => {

    const element = !tag ? null : document[tag] || document.querySelector(tag);

    if(element) window.$_swsk[tag] = element;
});

const visible = () => {

    if(!elements || !elements.length) return;

    let res = [];

    for(let id = 0; id < elements.length; id++) viewport(elements[id]) && res.push(window.$_swsk.refs[id]);

    window.$_swsk.viewport = res;
}

if(!!window.localStorage) {

    const local = window.localStorage.getItem(process.env.VUE_APP_STORAGE_KEY);

    if(local) try {
        stored = JSON.parse(local) || {};
    }
    catch(e) {
        stored = {};
    }
}

let sapo = reactive(stored);

window.$_swsk.widgets.state = {
    get: key => !key ? sapo : sapo[key],
    set: (key, value) => (sapo[key] = value)
};

const extend = main => {

    main.$dev = dev;

    main.$thumbs = obj => {

        const url = '//thumbs.web.sapo.io/?';
        let q = [];
        obj.src && q.push('p=' + encodeURIComponent(obj.src));
        obj.epic && q.push('epic=' + obj.epic);
        obj.hash && q.push('hash=' + obj.hash);
        obj.width && q.push('W=' + obj.width);
        obj.height && q.push('H=' + obj.height);
        obj.quality && q.push('Q=' + obj.quality);
        obj.crop && q.push('crop=' + obj.crop);
        q.push('errorpic=' + (obj.error ? encodeURIComponent(obj.error) : 'transparent'));
        obj.png && q.push('png=1');
        obj.optimize && q.push('optim=1');
        obj.delay && q.push('delay_optim=1');
        obj.tv && q.push('tv=' + obj.tv);
        return url + q.join('&');
    };

    main.$public = path;
};

const changed = args => {

    if(!args) return;

    let res = {
        ...(args || {}),
        type: 'widget'
    };

    if(!res.event) res.event = 'change-state';

    const done = () => {
        
        (dev || window.$_swsk.temp.dev) && console.warn({
            ...(res || {}),
            state: {
                ...(res.state || {})
            }
        });
        
        window.postMessage(JSON.stringify(res), "*");
    };

    if(res.event != 'change-state') done();
    else {
        
        clearTimeout(delays.state);
        
        delays.state = setTimeout(done, 250);
    }
}

const build = () => {

    test();

    window.addEventListener('resize', visible);
    
    window.addEventListener('scroll', visible);

    each(elements, (element, index) => {

        const id = !element ? null : element.getAttribute(selector('id'));

        if(id && !element.hasAttribute(selector('parsed')) && _data[id]) {

            element.setAttribute('id', id);

            element.setAttribute(selector('parsed'), true);
            
            let state = {
                ...(_data[id] || {}),
                ...(window.$_swsk.hash.get(id) || {}),
                update: 0,
                element: () => element,
                index,
                id
            };

            if(state.dev) {

                if(dev || beta) for(let prop in state.dev) state[prop] = state.dev[prop];

                delete state.dev;
            }

            if(state.view) {

                element.setAttribute(selector('view'), state.view);

                app.components = {
                    widget: defineAsyncComponent(() => import(`@/widgets/${state.view}/widget.vue`))
                };
                
                const main = createApp(app);

                for(let component in components) main.component(component, components[component]);
                
                for(let i = 0, atts = element.attributes, n = atts.length; i < n; i++) {

                    let key = atts[i].nodeName;
                    
                    if(key.startsWith(selector()) && atts[i].nodeValue) state[key.replace(selector(), '')] = atts[i].nodeValue;
                }

                main.config.globalProperties.$beta = beta;

                const name = capitalize(state.view);

                state.name = name;

                if(!window.$_swsk[name]) window.$_swsk[name] = [];

                state.internal = !element.hasAttribute('data-sapo-widget-dark') || element.getAttribute('data-sapo-widget-dark') != 'native'

                state.dark = state.internal ? element.classList.contains('dark') : (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches);

                window.$_swsk[name].push({
                    state: {
                        get: key => !key ? state : state[key],
                        set: (key, value) => (state[key] = value)
                    },
                    element,
                    name,
                    index,
                    id
                });

                let loading = {
                    counter: 0
                };

                const message = args => {

                    window.$_swsk.refs[index] = {
                        ...(args || {}),
                        index,
                        id,
                        name,
                        state: main.config.globalProperties.$state
                    };
                    
                    state.ready && changed(window.$_swsk.refs[index]);
                }

                main.config.globalProperties.$message = (event, value) => message({ event, value });

                main.config.globalProperties.$active = (args, clear) => {

                    !clear && message(args);

                    if(window.$_swsk.active) {

                        clearTimeout(delays[index]);

                        active[index] = !clear;

                        let actives = [];
                        
                        for(let i in active) if(active[i]) actives.push(window.$_swsk.refs[i]);

                        window.$_swsk.active = actives;

                        if(!clear) delays[index] = setTimeout(() => main.config.globalProperties.$active(null, true), 15000);
                    }
                };

                main.config.globalProperties.$_swsk = sapo;

                main.config.globalProperties.$loading = {
                    done: () => main.config.globalProperties.$loading.track(true),
                    track(done) {
                        !done ? loading.counter++ : loading.counter--;
                        clearTimeout(loading.delay);
                        loading.delay = setTimeout(() => {
                            state.ready = !loading.counter;
                            !loading.counter && message({ 
                                event: 'ready', 
                                value: true 
                            });
                        }, 150);
                    }
                };

                main.config.globalProperties.$kpis = (zone, type, frequency) => `${state.kpis || id}-${zone},${type || 'nav'},${frequency || 'static'}`;

                main.config.globalProperties.$state = reactive(state);

                extend(main.config.globalProperties);

                main.config.globalProperties.$widget = (key, value) => {
                    
                    if(!window.$_swsk[name] || !window.$_swsk[name][id]) return;
                    
                    window.$_swsk[name][id][key] = value;
                };

                main.mount(`[${selector(`id="${id}"`)}]`);
            }
        }
    });

    setTimeout(() => visible(), 1000);
};

const init = () => {

    const cached = `${process.env.VUE_APP_STORAGE_KEY}-v`;

    window.$_swsk.version = window.localStorage.getItem(cached) || process.env.VUE_APP_VERSION;

    if(!window.$_swsk.version) {
        
        build();

        return;
    }
 
    console.log(`current version: ${window.$_swsk.version}`);

    const script = document.createElement('script');

    script.setAttribute('src', `${path}app.js?v=${Math.random() * 10}`);

    script.onload = () => {

        const latest = !window.$app || !window.$app.src ? null : `${window.$app.version || ''}${window.$app.build || ''}`;

        if(!latest || latest == window.$_swsk.version) build();
        else {

            console.log(`latest version: ${latest}`);

            window.localStorage.setItem(cached, latest);
            
            const updated = document.createElement('script');
        
            updated.setAttribute('src', `${window.$app.src}?v=${latest}`);
    
            window.$_swsk.body.appendChild(updated);

            const outdated = document.querySelector(`[src="${window.$app.src}"]`);
    
            outdated && outdated.remove && outdated.remove();
        }
    };

    script.onerror = () => build();

    window.$_swsk.body.appendChild(script);
};

const test = () => {

    const prefix = '#widgets';

    if(window.location && window.location.hash.startsWith(prefix) && window.location.pathname == '/testes') {

        const main = document.querySelector('.main.page-blocks.page-blocks-testes');

        if(!main) return;

        let res = ['<div style="margin: 1rem 2rem">'];
        
        let widget = window.location.hash.replace(prefix, '').replace('-', '');

        for(let name in _data) if(!widget || widget == name) res.push(`<br/>
            ${!_data[name].title ? '' : `<h1 style="text-align:center;font-size:2.2em;color:#333;margin-bottom:0;">${_data[name].title}</h1>`}
            <h2 style="text-align:center;font-size:1em;color:#555;">${name}</h2><div ${selector('id')}="${name}">a carregar...</div>
        <br/>`);

        main.innerHTML = `${res.join('')}</div>`;

        elements = main.querySelectorAll(`[${selector('id')}]`);
    }
};

!dev ? init() : build();